import { useState, useEffect } from "react";
import {
    Row,
    Col,
    Card,
    message,
    Button,
    Tabs,
    Modal,
    Select,
    List,
    Tag,
    Space,
    Typography,
    Alert,
} from "antd";
import { PlusOutlined, EyeOutlined, ReloadOutlined } from "@ant-design/icons";
import {
    get_billing_plansApi,
    create_billing_planApi,
    update_billing_planApi,
    set_billing_plan_statusApi,
    get_billing_catalog_previewApi,
} from "../api/admin.billing.apis";
import {
    SubscriptionPlansTable,
    CreateSubscriptionPlanModal,
    EditSubscriptionPlanModal,
    BillingPricesPanel,
} from "../components/subscription-plans-management";

const { Text } = Typography;

function apiErrorMessage(error, fallback) {
    return (
        error?.response?.data?.message ||
        error?.message ||
        fallback
    );
}

function SubscriptionPlansManagement() {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("plans");

    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);

    const [selectedPlan, setSelectedPlan] = useState(null);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editLoading, setEditLoading] = useState(false);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewLang, setPreviewLang] = useState("en");
    const [previewItems, setPreviewItems] = useState([]);

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            setLoading(true);
            const response = await get_billing_plansApi();
            if (response.data.status === "success") {
                setPlans(response.data.data?.plans || []);
            } else {
                message.error(response.data.message || "Failed to load billing plans");
            }
        } catch (error) {
            console.error(error);
            message.error(apiErrorMessage(error, "Error loading billing plans"));
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (values) => {
        try {
            setCreateLoading(true);
            const response = await create_billing_planApi(values);
            if (response.data.status === "success") {
                message.success("Billing plan created");
                setCreateModalVisible(false);
                fetchPlans();
            } else {
                message.error(response.data.message || "Failed to create plan");
            }
        } catch (error) {
            console.error(error);
            message.error(apiErrorMessage(error, "Failed to create billing plan"));
        } finally {
            setCreateLoading(false);
        }
    };

    const handleEdit = async (values) => {
        try {
            setEditLoading(true);
            const response = await update_billing_planApi(selectedPlan.key, values);
            if (response.data.status === "success") {
                message.success("Billing plan updated");
                setEditModalVisible(false);
                setSelectedPlan(null);
                fetchPlans();
            } else {
                message.error(response.data.message || "Failed to update plan");
            }
        } catch (error) {
            console.error(error);
            message.error(apiErrorMessage(error, "Failed to update billing plan"));
        } finally {
            setEditLoading(false);
        }
    };

    const handleSetStatus = async (plan, status) => {
        try {
            const response = await set_billing_plan_statusApi(plan.key, status);
            if (response.data.status === "success") {
                message.success(
                    status === "active"
                        ? "Plan is active (public)"
                        : "Plan is inactive (hidden)"
                );
                fetchPlans();
            } else {
                message.error(response.data.message || "Failed to update status");
            }
        } catch (error) {
            console.error(error);
            message.error(apiErrorMessage(error, "Failed to update plan status"));
        }
    };

    const openPreview = async (language = previewLang) => {
        try {
            setPreviewLoading(true);
            setPreviewOpen(true);
            const response = await get_billing_catalog_previewApi(language);
            if (response.data.status === "success") {
                setPreviewItems(response.data.data?.plans || []);
            } else {
                message.error(response.data.message || "Failed to load preview");
            }
        } catch (error) {
            console.error(error);
            message.error(apiErrorMessage(error, "Failed to load catalog preview"));
        } finally {
            setPreviewLoading(false);
        }
    };

    return (
        <>
            <Row gutter={[24, 24]}>
                <Col span={24}>
                    <Card
                        title="Billing Plans Catalog"
                        extra={
                            <Space wrap>
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={fetchPlans}
                                    loading={loading}
                                >
                                    Refresh
                                </Button>
                                <Button
                                    icon={<EyeOutlined />}
                                    onClick={() => openPreview(previewLang)}
                                >
                                    Preview
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setCreateModalVisible(true)}
                                >
                                    Create Plan
                                </Button>
                            </Space>
                        }
                    >
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="Catalog only — limits are not enforced yet"
                            description="Status Active = shown on public pricing. Inactive = hidden. Set monthly prices on the Prices tab — yearly is auto-calculated from the Super Admin yearly discount %. Feature comparison bullets stay static on the customer frontend."
                        />
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            items={[
                                {
                                    key: "plans",
                                    label: `Plans (${plans.length})`,
                                    children: (
                                        <SubscriptionPlansTable
                                            plans={plans}
                                            loading={loading}
                                            onEdit={(plan) => {
                                                setSelectedPlan(plan);
                                                setEditModalVisible(true);
                                            }}
                                            onSetStatus={handleSetStatus}
                                        />
                                    ),
                                },
                                {
                                    key: "prices",
                                    label: "Prices",
                                    children: (
                                        <BillingPricesPanel plans={plans} />
                                    ),
                                },
                            ]}
                        />
                    </Card>
                </Col>
            </Row>

            <CreateSubscriptionPlanModal
                visible={createModalVisible}
                loading={createLoading}
                onSubmit={handleCreate}
                onClose={() => setCreateModalVisible(false)}
            />

            <EditSubscriptionPlanModal
                visible={editModalVisible}
                loading={editLoading}
                plan={selectedPlan}
                onSubmit={handleEdit}
                onClose={() => {
                    setEditModalVisible(false);
                    setSelectedPlan(null);
                }}
            />

            <Modal
                title="Catalog preview"
                open={previewOpen}
                onCancel={() => setPreviewOpen(false)}
                footer={null}
                width={760}
            >
                <Space style={{ marginBottom: 16 }}>
                    <Text type="secondary">Language</Text>
                    <Select
                        value={previewLang}
                        style={{ width: 140 }}
                        onChange={(lang) => {
                            setPreviewLang(lang);
                            openPreview(lang);
                        }}
                        options={[
                            { value: "en", label: "English" },
                            { value: "ar", label: "Arabic" },
                        ]}
                    />
                </Space>
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Public pricing only lists active plans that have at least one active monthly or yearly price."
                />
                <List
                    loading={previewLoading}
                    locale={{ emptyText: "No plans in catalog" }}
                    dataSource={previewItems}
                    renderItem={(item) => {
                        const hasPrice = Boolean(item.monthly || item.yearly);
                        const isActive = item.status === "active";
                        const onPublic = isActive && hasPrice;
                        let statusLabel = "Inactive (hidden)";
                        let statusColor = "default";
                        if (onPublic) {
                            statusLabel = "Active (public)";
                            statusColor = "success";
                        } else if (isActive && !hasPrice) {
                            statusLabel = "Active (not listed — add prices)";
                            statusColor = "warning";
                        }
                        return (
                        <List.Item>
                            <List.Item.Meta
                                title={
                                    <Space wrap>
                                        <Text strong>
                                            {item.key} — {item.name}
                                        </Text>
                                        <Tag color={statusColor}>{statusLabel}</Tag>
                                        {item.is_featured && (
                                            <Tag color="gold">Featured</Tag>
                                        )}
                                        {item.is_default && (
                                            <Tag color="blue">Default</Tag>
                                        )}
                                    </Space>
                                }
                                description={
                                    <Space direction="vertical" size={4}>
                                        <Text type="secondary">{item.description || "—"}</Text>
                                        <Text>
                                            AI: {item.limits?.ai_requests_monthly ?? "∞"} · Smart AI:{" "}
                                            {item.limits?.polar_summaries_monthly ?? "∞"} · Props:{" "}
                                            {item.limits?.ga4_properties ?? "∞"} · Members:{" "}
                                            {item.limits?.invited_members ?? "∞"} · Replays:{" "}
                                            {item.limits?.replay_sessions_month ?? "∞"}
                                        </Text>
                                        <Text>
                                            Monthly:{" "}
                                            {item.monthly
                                                ? `${item.monthly.currency} ${Number(
                                                      item.monthly.amount
                                                  ).toFixed(2)}`
                                                : "—"}{" "}
                                            · Yearly:{" "}
                                            {item.yearly
                                                ? `${item.yearly.currency} ${Number(
                                                      item.yearly.amount
                                                  ).toFixed(2)}`
                                                : "—"}
                                            {item.yearly_discount
                                                ? ` · Save ${item.yearly_discount.percentage}% (effective ${item.yearly.currency || item.monthly?.currency || "SAR"} ${Number(item.yearly_discount.annual_monthly || 0).toFixed(2)}/mo)`
                                                : ""}
                                        </Text>
                                    </Space>
                                }
                            />
                        </List.Item>
                        );
                    }}
                />
            </Modal>
        </>
    );
}

export default SubscriptionPlansManagement;
